import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { BrandMark, ThemeToggle, type ThemeMode } from '../components/Theme';

export default function Login({
  onAuth,
  theme,
  onThemeChange,
}: {
  onAuth: (token: string) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = challengeId
        ? await api.verifyLogin(challengeId, email, code)
        : await api.login(email, password);
      if (res.challenge) {
        setChallengeId(res.challenge.challenge_id);
        setCode('');
        return;
      }
      if (!res.token) {
        throw new Error('Login did not return a token');
      }
      localStorage.setItem('forge.token', res.token);
      onAuth(res.token);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="fixed right-4 top-4">
        <ThemeToggle value={theme} onChange={onThemeChange} />
      </div>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-8">
          <BrandMark size="lg" />
          <div className="text-xl font-semibold tracking-tight">GameForge</div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-4">
            {challengeId ? 'Enter login code' : 'Sign in'}
          </h2>

          {error && <div className="error-banner mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            {!challengeId ? (
              <>
                <div>
                  <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="input w-full"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    className="input w-full"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="text-xs font-semibold text-fg-dim uppercase tracking-wider block mb-1">
                  Code
                </label>
                <input
                  className="input w-full tracking-[0.3em]"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                />
              </div>
            )}
            <button type="submit" className="btn btn-primary w-full mt-2" disabled={loading}>
              {loading ? 'Signing in...' : challengeId ? 'Verify code' : 'Sign in'}
            </button>
          </form>

          <div className="text-center text-sm text-fg-dim mt-4">
            Don't have an account?{' '}
            <Link to="/signup" className="text-brand hover:underline">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
